import { SupabaseDB } from '@/utils/supabase';
import { message } from 'antd';
import type { 
  User, 
  Station, 
  Device, 
  Blog, 
  Command, 
  Metric, 
  MetricStation,
  Payment,
  Banner,
  StationPrice,
  LastStatusDevice,
  LastRevenue,
  LastStatusStation,
  UserFavoriteStation,
  DevicePriceLog,
  StationRequest,
  ContactOwner,
  GatewayStation,
  GatewayDevice,
  WebsiteContent,
  PageType,
  ContentType,
  Navigation,
  ConfigPlatform
} from '@/types/database';

// Generic service creator
const createService = <T>(tableName: string, idColumn: string = 'id') => {
  return {
    // Get all with optional filters
    getAll: async (filters?: any) => {
      try {
        const { data, error } = await SupabaseDB.select<T>(tableName, '*', filters);
        if (error) throw error;
        return { success: true, data: data || [] };
      } catch (error: any) {
        message.error(error.message || `Failed to fetch ${tableName}`);
        return { success: false, message: error.message, data: [] };
      }
    },

    // Get by ID
    getById: async (id: any) => {
      try {
        const { data, error } = await SupabaseDB.select<T>(tableName, '*', { [idColumn]: id });
        if (error) throw error;
        return { success: true, data: data?.[0] || null };
      } catch (error: any) {
        message.error(error.message || `Failed to fetch ${tableName}`);
        return { success: false, message: error.message, data: null };
      }
    },

    // Create
    create: async (record: Partial<T>) => {
      try {
        const { data, error } = await SupabaseDB.insert<T>(tableName, record);
        if (error) throw error;
        message.success('Created successfully');
        return { success: true, data: data?.[0] || null };
      } catch (error: any) {
        message.error(error.message || 'Create failed');
        return { success: false, message: error.message, data: null };
      }
    },

    // Update
    update: async (id: any, record: Partial<T>) => {
      try {
        const { data, error } = await SupabaseDB.update<T>(tableName, id, record, idColumn);
        if (error) throw error;
        message.success('Updated successfully');
        return { success: true, data: data?.[0] || null };
      } catch (error: any) {
        message.error(error.message || 'Update failed');
        return { success: false, message: error.message, data: null };
      }
    },

    // Delete
    delete: async (id: any) => {
      try {
        const { error } = await SupabaseDB.delete(tableName, id, idColumn);
        if (error) throw error;
        message.success('Deleted successfully');
        return { success: true };
      } catch (error: any) {
        message.error(error.message || 'Delete failed');
        return { success: false, message: error.message };
      }
    },

    // Custom query
    query: () => SupabaseDB.customQuery(tableName),
  };
};

// User service
export const userService = {
  ...createService<User>('users', 'user_id'),
  
  // Get users with station info
  getUsersWithStations: async () => {
    try {
      const query = SupabaseDB.customQuery('users')
        .select('*, stations(*)')
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Station service
export const stationService = {
  ...createService<Station>('stations', 'station_id'),
  
  // Get stations with devices
  getStationsWithDevices: async () => {
    try {
      const query = SupabaseDB.customQuery('stations')
        .select('*, devices(*)')
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get station with last status
  getStationWithStatus: async (stationId: string) => {
    try {
      const query = SupabaseDB.customQuery('stations')
        .select('*, last_status_station(*), devices(*)')
        .eq('station_id', stationId)
        .single();
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: null };
    }
  },

  // Get all stations with status
  getAllStationsWithStatus: async () => {
    try {
      const query = SupabaseDB.customQuery('stations')
        .select('*, last_status_station(*)')
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Station Price service
export const stationPriceService = createService<StationPrice>('station_price', 'station_id');

// Device service
export const deviceService = {
  ...createService<Device>('devices', 'device_id'),
  
  // Get devices by station
  getByStation: async (stationId: string) => {
    try {
      const { data, error } = await SupabaseDB.select<Device>('devices', '*', { station_id: stationId });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get device with last status
  getDeviceWithStatus: async (deviceId: string) => {
    try {
      const query = SupabaseDB.customQuery('devices')
        .select('*, last_status_device(*), stations(*)')
        .eq('device_id', deviceId)
        .single();
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: null };
    }
  },
};

// Command service
export const commandService = {
  ...createService<Command>('commands', 'cmd_id'),
  
  // Get commands by device
  getByDevice: async (deviceId: string) => {
    try {
      const { data, error } = await SupabaseDB.select<Command>('commands', '*', { device_id: deviceId });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Last Status Device service
export const lastStatusDeviceService = createService<LastStatusDevice>('last_status_device', 'device_id');

// Last Revenue service
export const lastRevenueService = createService<LastRevenue>('last_revenue', 'station_id');

// Last Status Station service
export const lastStatusStationService = createService<LastStatusStation>('last_status_station', 'station_id');

// Blog service
export const blogService = {
  ...createService<Blog>('blogs', 'id'),
  
  // Get active blogs
  getActive: async () => {
    try {
      const { data, error } = await SupabaseDB.select<Blog>('blogs', '*', { active: true });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Banner service
export const bannerService = {
  ...createService<Banner>('banners', 'id'),
  
  // Get active banners
  getActive: async () => {
    try {
      const { data, error } = await SupabaseDB.select<Banner>('banners', '*', { active: true });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Metric service
export const metricService = {
  ...createService<Metric>('metrics', 'id'),
  
  // Get metrics by device
  getByDevice: async (deviceId: string, limit: number = 100) => {
    try {
      const query = SupabaseDB.customQuery('metrics')
        .select('*')
        .eq('device_id', deviceId)
        .order('created_at', { ascending: false })
        .limit(limit);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get metrics by station
  getByStation: async (stationId: string, limit: number = 100) => {
    try {
      const query = SupabaseDB.customQuery('metrics')
        .select('*')
        .eq('station_id', stationId)
        .order('created_at', { ascending: false })
        .limit(limit);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get metrics with date range
  getByDateRange: async (startDate: string, endDate: string, filters?: any) => {
    try {
      let query = SupabaseDB.customQuery('metrics')
        .select('*')
        .gte('created_at', startDate)
        .lte('created_at', endDate);
      
      if (filters?.device_id) {
        query = query.eq('device_id', filters.device_id);
      }
      if (filters?.station_id) {
        query = query.eq('station_id', filters.station_id);
      }
      
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Metric Station service
export const metricStationService = {
  ...createService<MetricStation>('metrics_station', 'id'),
  
  // Get metrics by station
  getByStation: async (stationId: string, limit: number = 100) => {
    try {
      const query = SupabaseDB.customQuery('metrics_station')
        .select('*')
        .eq('station_id', stationId)
        .order('created_at', { ascending: false })
        .limit(limit);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Payment service
export const paymentService = {
  ...createService<Payment>('payments', 'id'),
  
  // Get payments by device
  getByDevice: async (deviceId: string) => {
    try {
      const query = SupabaseDB.customQuery('payments')
        .select('*')
        .eq('device_id', deviceId)
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get payments by station
  getByStation: async (stationId: string) => {
    try {
      const query = SupabaseDB.customQuery('payments')
        .select('*')
        .eq('station_id', stationId)
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get payments by buyer
  getByBuyer: async (buyerId: string) => {
    try {
      const query = SupabaseDB.customQuery('payments')
        .select('*')
        .eq('buyer_id', buyerId)
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// User Favorite Station service
export const userFavoriteStationService = {
  ...createService<UserFavoriteStation>('user_favorite_stations', 'user_id'),
  
  // Get favorite stations by user
  getByUser: async (userId: string) => {
    try {
      const query = SupabaseDB.customQuery('user_favorite_stations')
        .select('*, stations(*)')
        .eq('user_id', userId);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Add favorite station
  addFavorite: async (userId: string, stationId: string) => {
    try {
      const now = Math.floor(Date.now() / 1000); // Unix timestamp
      const { data, error } = await SupabaseDB.insert<UserFavoriteStation>(
        'user_favorite_stations',
        { user_id: userId, station_id: stationId, created_at: now }
      );
      if (error) throw error;
      message.success('Added to favorites');
      return { success: true, data: data?.[0] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message };
    }
  },

  // Remove favorite station
  removeFavorite: async (userId: string, stationId: string) => {
    try {
      const query = SupabaseDB.customQuery('user_favorite_stations')
        .delete()
        .eq('user_id', userId)
        .eq('station_id', stationId);
      const { error } = await query;
      if (error) throw error;
      message.success('Removed from favorites');
      return { success: true };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message };
    }
  },
};

// Device Price Log service
export const devicePriceLogService = {
  ...createService<DevicePriceLog>( 'device_price_logs', 'id' ),
  
  getPriceLogRefStation: async ( stationId: string ) => {
    try {
      const query = SupabaseDB.customQuery('device_price_logs')
        .select('*, author:users(user_id, first_name, last_name)')
        .eq('station_id', stationId)
        .order('created_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
      
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message };
    }
  }
};

// Station Request service
export const stationRequestService = {
  ...createService<StationRequest>('station_request', 'id'),
  
  // Get requests by status
  getByStatus: async (status: string) => {
    try {
      const { data, error } = await SupabaseDB.select<StationRequest>('station_request', '*', { status_request: status });
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
  
  // Update status (for simple status changes without full flow)
  updateStatus: async (id: number, status: string, note?: string) => {
    try {
      const updateData: any = {
        status_request: status,
        updated_at: Math.floor(Date.now() / 1000),
      };
      
      const { data, error } = await SupabaseDB.update<StationRequest>('station_request', id, updateData, 'id');
      if (error) throw error;
      message.success(`Đã ${status === 'approved' ? 'duyệt' : status === 'reject' ? 'từ chối' : 'cập nhật'} yêu cầu`);
      return { success: true, data: data?.[0] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message };
    }
  },
  
  // Accept station request via Supabase Database Function (RPC)
  // Flow:
  // 1. Check if email exists in auth.users
  // 2. Create user in auth.users (if new) with auto email_confirm
  // 3. Create user in public.users with first_name, last_name from payment_info
  // 4. Assign "Chủ trạm" role to user
  // 5. Create station in public.stations
  // 6. Link station to user (update users.stations array)
  // 7. Update request status to approved
  acceptRequest: async (request: StationRequest, gatewayStationId?: string) => {
    try {
      const { getSupabaseClient } = await import('@/utils/supabase');
      const supabase = getSupabaseClient();
      
      if (!request.email) {
        throw new Error('Email không tồn tại trong yêu cầu');
      }
      
      if (!gatewayStationId) {
        throw new Error('Gateway Station ID không được để trống');
      }
      
      // Call Database Function (RPC) to handle the approval process
      const { data, error } = await supabase.rpc('approve_station_request', {
        p_request_id: request.id,
        p_admin_note: null,
        p_gateway_station_id: gatewayStationId
      });
      
      if (error) {
        throw new Error(error.message || 'Có lỗi xảy ra khi gọi database function');
      }
      
      // data is the JSONB result from the function
      const result = data as {
        success: boolean;
        message?: string;
        error?: string;
        error_detail?: string;
        data?: {
          request_id: number;
          user_id: string;
          is_new_user: boolean;
          user_email: string;
          generated_password: string | null;
          station_id: string;
          station_name: string;
        };
      };
      
      if (!result.success) {
        throw new Error(result.error || 'Database function trả về lỗi');
      }
      
      const resultData = result.data!;
      
      return {
        success: true,
        data: {
          userId: resultData.user_id,
          stationId: resultData.station_id,
          stationName: resultData.station_name,
          isNewUser: resultData.is_new_user,
          email: resultData.user_email,
          password: resultData.generated_password, // Only for new users, null otherwise
        }
      };
    } catch (error: any) {
      console.error('Error accepting request:', error);
      message.error(error.message || 'Có lỗi xảy ra khi duyệt yêu cầu');
      return { success: false, message: error.message };
    }
  },
  
  // Reject station request via Supabase Database Function (RPC)
  rejectRequest: async (requestId: number, rejectReason?: string) => {
    try {
      const { getSupabaseClient } = await import('@/utils/supabase');
      const supabase = getSupabaseClient();
      
      // Call Database Function (RPC) to handle the rejection
      const { data, error } = await supabase.rpc('reject_station_request', {
        p_request_id: requestId,
        p_reject_reason: rejectReason || null
      });
      
      if (error) {
        throw new Error(error.message || 'Có lỗi xảy ra khi gọi database function');
      }
      
      const result = data as {
        success: boolean;
        message?: string;
        error?: string;
        data?: {
          request_id: number;
          station_name: string;
          email: string;
          reject_reason: string | null;
        };
      };
      
      if (!result.success) {
        throw new Error(result.error || 'Database function trả về lỗi');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      message.error(error.message || 'Có lỗi xảy ra khi từ chối yêu cầu');
      return { success: false, message: error.message };
    }
  },
};

// Contact Owner service - chỉ có 1 record duy nhất
export const contactOwnerService = {
  ...createService<ContactOwner>('contact_owner', 'id'),

  // Get the single contact owner record
  getContactOwner: async () => {
    try {
      const query = SupabaseDB.customQuery('contact_owner')
        .select('*')
        .limit(1);
      const { data, error } = await query;
      if (error) throw error;
      // Return first item or null if no data
      return { success: true, data: data && data.length > 0 ? data[0] : null };
    } catch (error: any) {
      console.error('Error fetching contact owner:', error);
      return { success: false, message: error.message, data: null };
    }
  },

  // Create or update contact owner (upsert logic)
  upsert: async (record: Partial<ContactOwner>) => {
    try {
      // First check if record exists
      const query = SupabaseDB.customQuery('contact_owner')
        .select('id')
        .limit(1);
      const { data: existing, error: fetchError } = await query;
      
      if (fetchError) throw fetchError;

      if (existing && existing.length > 0) {
        // Update existing record
        const { data, error } = await SupabaseDB.update<ContactOwner>(
          'contact_owner',
          existing[0].id,
          { ...record, updated_at: new Date().toISOString() },
          'id',
        );
        if (error) throw error;
        message.success('Cập nhật thông tin liên hệ thành công');
        return { success: true, data: data?.[0] || null };
      } else {
        // Create new record
        const { data, error } = await SupabaseDB.insert<ContactOwner>('contact_owner', {
          ...record,
        });
        if (error) throw error;
        message.success('Tạo thông tin liên hệ thành công');
        return { success: true, data: data?.[0] || null };
      }
    } catch (error: any) {
      message.error(error.message || 'Lưu thông tin liên hệ thất bại');
      return { success: false, message: error.message, data: null };
    }
  },
};

// Gateway Station service
export const gatewayStationService = {
  ...createService<GatewayStation>('list_gateway_available', 'station_id'),

  // Get active gateways only
  getActive: async () => {
    try {
      const { data, error } = await SupabaseDB.select<GatewayStation>(
        'list_gateway_available',
        '*',
        { active: true },
      );
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get gateway with station info
  getWithStationInfo: async () => {
    try {
      // Get all gateway stations
      const { data: gateways, error: gatewayError } =
        await SupabaseDB.select<GatewayStation>('list_gateway_available', '*');

      if (gatewayError) throw gatewayError;

      if (!gateways || gateways.length === 0) {
        return { success: true, data: [] };
      }

      // Get all stations
      const { data: stations, error: stationError } =
        await SupabaseDB.select<Station>('stations', '*');

      if (stationError) throw stationError;

      // Manually join the data
      const gatewaysWithStations = gateways.map((gateway) => {
        const station = stations?.find(
          (s) => s.station_id === gateway.station_id,
        );
        return {
          ...gateway,
          stations: station || null,
        };
      });

      // Sort by created_at descending
      gatewaysWithStations.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      return { success: true, data: gatewaysWithStations };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Gateway Device service
export const gatewayDeviceService = {
  ...createService<GatewayDevice>('list_devices_available', 'device_id'),

  // Get active gateways only
  getActive: async () => {
    try {
      const { data, error } = await SupabaseDB.select<GatewayDevice>(
        'list_devices_available',
        '*',
        { active: true },
      );
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Get gateway with device info
  getWithDeviceInfo: async () => {
    try {
      // Get all gateway devices
      const { data: gateways, error: gatewayError } =
        await SupabaseDB.select<GatewayDevice>('list_devices_available', '*');

      if (gatewayError) throw gatewayError;

      if (!gateways || gateways.length === 0) {
        return { success: true, data: [] };
      }

      // Get all devices
      const { data: devices, error: deviceError } =
        await SupabaseDB.select<Device>('devices', '*');

      if (deviceError) throw deviceError;

      // Manually join the data
      const gatewaysWithDevices = gateways.map((gateway) => {
        const device = devices?.find(
          (d) => d.device_id === gateway.device_id,
        );
        return {
          ...gateway,
          devices: device || null,
        };
      });

      // Sort by created_at descending
      gatewaysWithDevices.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      return { success: true, data: gatewaysWithDevices };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Website Content service - Quản lý nội dung các trang website
export const websiteContentService = {
  ...createService<WebsiteContent>('website_content', 'id'),

  // Lấy nội dung theo trang
  getByPage: async (pageType: PageType) => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('page_type', pageType)
        .eq('active', true)
        .order('display_order', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Lấy nội dung theo loại
  getByContentType: async (contentType: ContentType) => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('content_type', contentType)
        .eq('active', true)
        .order('display_order', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Lấy nội dung theo trang và loại
  getByPageAndType: async (pageType: PageType, contentType: ContentType) => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('page_type', pageType)
        .eq('content_type', contentType)
        .eq('active', true)
        .order('display_order', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Lấy banner của một trang
  getBanner: async (pageType: PageType) => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('page_type', pageType)
        .eq('content_type', 'banner')
        .eq('active', true)
        .order('display_order', { ascending: true })
        .limit(1);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: null };
    }
  },

  // Lấy thống kê trang chủ
  getHomeStatistics: async () => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('page_type', 'home')
        .eq('content_type', 'statistic')
        .eq('active', true)
        .order('display_order', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Lấy hướng dẫn sạc
  getGuideSteps: async () => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('page_type', 'home')
        .eq('content_type', 'guide_step')
        .eq('active', true)
        .order('step_number', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Lấy footer links
  getFooterLinks: async () => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('page_type', 'footer')
        .eq('content_type', 'link')
        .eq('active', true)
        .order('display_order', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },

  // Lấy content bài viết theo url
  getArticleByUrl: async (url: string) => {
    try {
      const query = SupabaseDB.customQuery('website_content')
        .select('*')
        .eq('content_type', 'article')
        .eq('url', url)
        .eq('active', true)
        .limit(1);
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: null };
    }
  },

  // Lấy tất cả nội dung (bao gồm cả inactive) - dùng cho admin
  getAllForAdmin: async (filters?: { page_type?: PageType; content_type?: ContentType }) => {
    try {
      let query = SupabaseDB.customQuery('website_content')
        .select('*');
      
      if (filters?.page_type) {
        query = query.eq('page_type', filters.page_type);
      }
      if (filters?.content_type) {
        query = query.eq('content_type', filters.content_type);
      }
      
      query = query.order('page_type').order('content_type').order('display_order', { ascending: true });
      
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
};

// Navigation service - Quản lý menu điều hướng
export const navigationService = {
  ...createService<Navigation>('navigations', 'nav_id'),
  
  // Lấy tất cả navigation với cấu trúc phân cấp
  getAllWithHierarchy: async (activeOnly: boolean = false) => {
    try {
      let query = SupabaseDB.customQuery('navigations')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (activeOnly) {
        query = query.eq('active', true);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // Build hierarchy
      const navigations = data || [];
      const navigationMap = new Map<number, Navigation>();
      const rootNavigations: Navigation[] = [];
      
      // First pass: create map
      navigations.forEach((nav: Navigation) => {
        navigationMap.set(nav.nav_id, { ...nav, children: [] });
      });
      
      // Second pass: build hierarchy
      navigations.forEach((nav: Navigation) => {
        const navWithChildren = navigationMap.get(nav.nav_id)!;
        if (nav.parent_id) {
          const parent = navigationMap.get(nav.parent_id);
          if (parent) {
            parent.children!.push(navWithChildren);
          } else {
            rootNavigations.push(navWithChildren);
          }
        } else {
          rootNavigations.push(navWithChildren);
        }
      });
      
      return { success: true, data: rootNavigations };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
  
  // Lấy navigation theo parent_id
  getByParentId: async (parentId: number | null) => {
    try {
      let query = SupabaseDB.customQuery('navigations')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (parentId === null) {
        query = query.is('parent_id', null);
      } else {
        query = query.eq('parent_id', parentId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      message.error(error.message);
      return { success: false, message: error.message, data: [] };
    }
  },
  
  // Cập nhật thứ tự hiển thị
  updateDisplayOrder: async (navId: number, newOrder: number) => {
    try {
      const { data, error } = await SupabaseDB.update<Navigation>(
        'navigations',
        navId,
        { display_order: newOrder },
        'nav_id'
      );
      if (error) throw error;
      message.success('Đã cập nhật thứ tự hiển thị');
      return { success: true, data: data?.[0] || null };
    } catch (error: any) {
      message.error(error.message || 'Cập nhật thứ tự thất bại');
      return { success: false, message: error.message, data: null };
    }
  },
  
  // Toggle active status
  toggleActive: async (navId: number, active: boolean) => {
    try {
      const { data, error } = await SupabaseDB.update<Navigation>(
        'navigations',
        navId,
        { active },
        'nav_id'
      );
      if (error) throw error;
      message.success(active ? 'Đã kích hoạt' : 'Đã ẩn');
      return { success: true, data: data?.[0] || null };
    } catch (error: any) {
      message.error(error.message || 'Cập nhật trạng thái thất bại');
      return { success: false, message: error.message, data: null };
    }
  },
};

// Config Platform service
export const configPlatformService = {
  ...createService<ConfigPlatform>('config_platform', 'key'),

  // Lấy tất cả cấu hình, sắp xếp theo key
  getAllOrdered: async () => {
    try {
      const { data, error } = await SupabaseDB.customQuery('config_platform')
        .select('*')
        .order('key', { ascending: true });
      if (error) throw error;
      return { success: true, data: (data || []) as ConfigPlatform[] };
    } catch (error: any) {
      message.error(error.message || 'Tải cấu hình thất bại');
      return { success: false, message: error.message, data: [] as ConfigPlatform[] };
    }
  },

  // Toggle active status
  toggleActive: async (key: string, is_active: boolean) => {
    try {
      const { data, error } = await SupabaseDB.update<ConfigPlatform>(
        'config_platform',
        key,
        { is_active, updated_at: new Date().toISOString() },
        'key'
      );
      if (error) throw error;
      // RLS chặn update sẽ không trả lỗi mà trả về mảng rỗng
      if (!data || data.length === 0) {
        throw new Error('Không có quyền cập nhật cấu hình (kiểm tra RLS policy của bảng config_platform)');
      }
      message.success(is_active ? 'Đã kích hoạt' : 'Đã tắt');
      return { success: true, data: data[0] };
    } catch (error: any) {
      message.error(error.message || 'Cập nhật trạng thái thất bại');
      return { success: false, message: error.message, data: null };
    }
  },

  // Cập nhật giá trị (value) và mô tả
  updateConfig: async (key: string, record: Pick<ConfigPlatform, 'value' | 'description'>) => {
    try {
      const { data, error } = await SupabaseDB.update<ConfigPlatform>(
        'config_platform',
        key,
        { ...record, updated_at: new Date().toISOString() },
        'key'
      );
      if (error) throw error;
      // RLS chặn update sẽ không trả lỗi mà trả về mảng rỗng
      if (!data || data.length === 0) {
        throw new Error('Không có quyền cập nhật cấu hình (kiểm tra RLS policy của bảng config_platform)');
      }
      message.success('Cập nhật cấu hình thành công');
      return { success: true, data: data[0] };
    } catch (error: any) {
      message.error(error.message || 'Cập nhật cấu hình thất bại');
      return { success: false, message: error.message, data: null };
    }
  },
};

export default {
  userService,
  stationService,
  stationPriceService,
  deviceService,
  commandService,
  lastStatusDeviceService,
  lastRevenueService,
  lastStatusStationService,
  blogService,
  bannerService,
  metricService,
  metricStationService,
  paymentService,
  userFavoriteStationService,
  devicePriceLogService,
  stationRequestService,
  contactOwnerService,
  gatewayStationService,
  gatewayDeviceService,
  websiteContentService,
  navigationService,
  configPlatformService,
};
